// src/components/notifications/NotificationItem.jsx
//
// A single notification card. Collapsed it shows a title + short message; click
// it (or its chevron) to expand the full diagnostic detail — HTTP status, error
// code, request id, the failing request, the server response body, and the JS
// stack trace — plus a "Copy details" button so a user can paste it into a bug
// report. Info/success toasts auto-dismiss; errors and warnings stay until the
// user closes them.
//
// The expandable area is a real <button> with the close button as a SIBLING
// (not nested), so it's keyboard-accessible and lint-clean.
import React, { useCallback, useEffect, useRef, useState } from 'react';

const ICONS = { error: '⨯', warning: '!', info: 'i', success: '✓' };
const AUTO_DISMISS_MS = { success: 5000, info: 7000 };
// Keep in sync with --qd-transition-slow (the qd-notif-out exit animation).
const EXIT_MS = 300;

function formatTime(ts) {
    try {
        return new Date(ts).toLocaleTimeString();
    } catch {
        return '';
    }
}

function buildClipboardText(n) {
    const lines = [];
    lines.push(`[${(n.type || 'error').toUpperCase()}] ${n.title}`);
    if (n.message) lines.push(n.message);
    lines.push('');
    if (n.status != null) lines.push(`Status: ${n.status}${n.statusText ? ` ${n.statusText}` : ''}`);
    if (n.code) lines.push(`Code: ${n.code}`);
    if (n.requestId) lines.push(`Request ID: ${n.requestId}`);
    if (n.method || n.url) lines.push(`Request: ${`${n.method || ''} ${n.url || ''}`.trim()}`);
    if (n.timestamp) lines.push(`Time: ${new Date(n.timestamp).toISOString()}`);
    if (n.detail) {
        lines.push(`Detail: ${typeof n.detail === 'string' ? n.detail : JSON.stringify(n.detail)}`);
    }
    if (n.responseBody !== undefined) {
        lines.push(`Response: ${JSON.stringify(n.responseBody, null, 2)}`);
    }
    if (n.stack) {
        lines.push('', 'Stack:', n.stack);
    }
    return lines.join('\n');
}

const NotificationItem = ({ notification: n, onDismiss }) => {
    const [expanded, setExpanded] = useState(false);
    const [copied, setCopied] = useState(false);
    const [leaving, setLeaving] = useState(false);
    const exitTimerRef = useRef(null);

    const type = n.type || 'error';

    useEffect(() => () => clearTimeout(exitTimerRef.current), []);

    // Play the collapse/exit animation, then actually remove the card. Respects
    // reduced-motion (the CSS swaps to a quick fade) — the timer is the same.
    const dismiss = useCallback(() => {
        if (exitTimerRef.current) return; // already leaving
        setLeaving(true);
        exitTimerRef.current = setTimeout(() => onDismiss(n.id), EXIT_MS);
    }, [n.id, onDismiss]);
    const hasDetails = !!(
        n.status != null ||
        n.code ||
        n.requestId ||
        n.url ||
        n.stack ||
        n.responseBody !== undefined ||
        n.detail
    );

    // Auto-dismiss non-critical types (unless the user expanded the card).
    // n.timestamp is a dep so a de-dupe hit (same id, bumped timestamp) re-arms
    // the countdown instead of letting the original timer fire mid-storm.
    useEffect(() => {
        const ms = AUTO_DISMISS_MS[type];
        if (!ms || expanded) return undefined;
        const t = setTimeout(dismiss, ms);
        return () => clearTimeout(t);
    }, [type, expanded, n.id, n.timestamp, dismiss]);

    const handleCopy = async () => {
        const text = buildClipboardText(n);
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            // Fallback for non-secure / unsupported contexts.
            const ta = document.createElement('textarea');
            ta.value = text;
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            try {
                document.execCommand('copy');
            } catch {
                /* nothing else to try */
            }
            ta.remove();
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const toggle = () => setExpanded((v) => !v);

    const inner = (
        <>
            <span className="qd-notif-icon" aria-hidden="true">
                {ICONS[type] || '!'}
            </span>
            <span className="qd-notif-text">
                <span className="qd-notif-title">
                    {n.title}
                    {n.count > 1 && <span className="qd-notif-count">×{n.count}</span>}
                </span>
                {n.message && <span className="qd-notif-message">{n.message}</span>}
            </span>
            {hasDetails && (
                <span className={`qd-notif-chevron${expanded ? ' open' : ''}`} aria-hidden="true">
                    ▾
                </span>
            )}
        </>
    );

    return (
        <div
            className={`qd-notif qd-notif--${type}${expanded ? ' qd-notif--expanded' : ''}${
                leaving ? ' qd-notif--leaving' : ''
            }`}
        >
            <div className="qd-notif-head">
                {hasDetails ? (
                    <button
                        type="button"
                        className="qd-notif-main"
                        onClick={toggle}
                        aria-expanded={expanded}
                        title="Click for details"
                    >
                        {inner}
                    </button>
                ) : (
                    <div className="qd-notif-main qd-notif-main--static">{inner}</div>
                )}
                <button
                    type="button"
                    className="qd-notif-close"
                    aria-label="Dismiss notification"
                    onClick={dismiss}
                >
                    ×
                </button>
            </div>

            {expanded && hasDetails && (
                <div className="qd-notif-details">
                    <dl className="qd-notif-meta">
                        {n.status != null && (
                            <>
                                <dt>Status</dt>
                                <dd>
                                    {n.status} {n.statusText}
                                </dd>
                            </>
                        )}
                        {n.code && (
                            <>
                                <dt>Code</dt>
                                <dd className="qd-notif-mono">{n.code}</dd>
                            </>
                        )}
                        {n.requestId && (
                            <>
                                <dt>Request ID</dt>
                                <dd className="qd-notif-mono">{n.requestId}</dd>
                            </>
                        )}
                        {(n.method || n.url) && (
                            <>
                                <dt>Request</dt>
                                <dd className="qd-notif-mono">
                                    {n.method} {n.url}
                                </dd>
                            </>
                        )}
                        <>
                            <dt>Time</dt>
                            <dd>{formatTime(n.timestamp)}</dd>
                        </>
                    </dl>

                    {n.detail && (
                        <pre className="qd-notif-pre">
                            {typeof n.detail === 'string' ? n.detail : JSON.stringify(n.detail, null, 2)}
                        </pre>
                    )}

                    {n.responseBody !== undefined && (
                        <>
                            <div className="qd-notif-section-label">Response</div>
                            <pre className="qd-notif-pre">{JSON.stringify(n.responseBody, null, 2)}</pre>
                        </>
                    )}

                    {n.stack && (
                        <>
                            <div className="qd-notif-section-label">Stack trace</div>
                            <pre className="qd-notif-pre qd-notif-trace">{n.stack}</pre>
                        </>
                    )}

                    <div className="qd-notif-actions">
                        <button type="button" className="qd-notif-copy" onClick={handleCopy}>
                            {copied ? 'Copied ✓' : 'Copy details'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationItem;
